import React, { forwardRef, useState, useEffect, useCallback } from 'react'
import {
  Card,
  Text,
  Stack,
  Box,
  Button,
  Spinner,
  Flex,
  Badge,
  Checkbox,
  Select,
  Label,
} from '@sanity/ui'
import { ObjectInputProps, useFormValue, useClient, Path } from 'sanity'
import { SyncIcon } from '@sanity/icons'

interface DescriptionResult {
  generated: boolean
  skipped?: boolean
  reason?: string
  content?: string
  error?: string
}

interface ListingResult {
  fitmentTypeIndex: number
  listingIndex: number
  wheelSetup: string
  wheelSpecs: {
    front: string
    rear: string
  }
  descriptions: Record<string, DescriptionResult>
}

interface GenerationResponse {
  success: boolean
  summary: {
    totalListings: number
    processed: number
    generated: number
    skipped: number
    failed: number
  }
  results: ListingResult[]
  metadata: {
    totalCost: number
    totalTokens: number
    processingTimeMs: number
  }
  rules?: {
    id: string
    name: string
    version: string
  }
}

interface AIRuleset {
  _id: string
  name: string
  version: string
  isActive: boolean
}

const DESCRIPTION_TYPE_LABELS: Record<string, string> = {
  overview: 'Overview',
  performanceAnalysis: 'Performance Analysis',
  easeOfFitment: 'Ease of Fitment',
  tireAvailability: 'Tire Availability',
}

/**
 * Extracts _key from a keyed segment
 */
function getKeyFromSegment(segment: unknown): string | null {
  if (typeof segment === 'object' && segment !== null && '_key' in segment) {
    return (segment as { _key: string })._key
  }
  return null
}

/**
 * Parses the Sanity form path to extract fitmentType key and listing key.
 * Path can contain numeric indices OR keyed segments {_key: '...'}.
 */
function parsePathKeys(path: Path): {
  fitmentTypeKey: string | null
  listingKey: string | null
  fitmentTypeIndex: number | null
  listingIndex: number | null
} {
  let fitmentTypeKey: string | null = null
  let listingKey: string | null = null
  let fitmentTypeIndex: number | null = null
  let listingIndex: number | null = null

  for (let i = 0; i < path.length; i++) {
    const segment = path[i]
    const nextSegment = path[i + 1]

    if (segment === 'fitmentTypes') {
      if (typeof nextSegment === 'number') {
        fitmentTypeIndex = nextSegment
      } else {
        fitmentTypeKey = getKeyFromSegment(nextSegment)
      }
    }
    if (segment === 'listingItems') {
      if (typeof nextSegment === 'number') {
        listingIndex = nextSegment
      } else {
        listingKey = getKeyFromSegment(nextSegment)
      }
    }
  }

  return { fitmentTypeKey, listingKey, fitmentTypeIndex, listingIndex }
}

const ListingAIRegenerateButton = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    const { path } = props

    // Get document ID using Sanity v4 hook
    const rawId = useFormValue(['_id']) as string | undefined
    const documentId = rawId?.replace('drafts.', '')
    const client = useClient({ apiVersion: '2024-01-01' })

    // Get fitmentTypes array to resolve keys to indices
    const fitmentTypes = useFormValue(['fitmentTypes']) as Array<{ _key: string; listingItems?: Array<{ _key: string }> }> | undefined

    // Parse path to get keys/indices
    const { fitmentTypeKey, listingKey, fitmentTypeIndex: parsedFtIndex, listingIndex: parsedLIndex } = parsePathKeys(path)

    // Resolve keys to indices if needed
    let fitmentTypeIndex: number | null = parsedFtIndex
    let listingIndex: number | null = parsedLIndex

    if (fitmentTypeKey && fitmentTypes) {
      fitmentTypeIndex = fitmentTypes.findIndex(ft => ft._key === fitmentTypeKey)
      if (fitmentTypeIndex === -1) fitmentTypeIndex = null
    }

    if (listingKey && fitmentTypes && fitmentTypeIndex !== null) {
      const listingItems = fitmentTypes[fitmentTypeIndex]?.listingItems
      if (listingItems) {
        listingIndex = listingItems.findIndex(li => li._key === listingKey)
        if (listingIndex === -1) listingIndex = null
      }
    }

    // Ruleset state
    const [rulesets, setRulesets] = useState<AIRuleset[]>([])
    const [selectedRulesId, setSelectedRulesId] = useState<string>('')
    const [loadingRulesets, setLoadingRulesets] = useState(true)

    // Generation state
    const [loading, setLoading] = useState(false)
    const [results, setResults] = useState<GenerationResponse | null>(null)
    const [error, setError] = useState('')
    const [forceRegenerate, setForceRegenerate] = useState(false)

    // Fetch available rulesets on mount
    useEffect(() => {
      const fetchRulesets = async () => {
        try {
          const result = await client.fetch<AIRuleset[]>(
            `*[_type == "aiGenerationRules"]{ _id, name, version, isActive } | order(isActive desc, name asc)`
          )
          setRulesets(result || [])
          // Set default to active ruleset or first available
          const activeRuleset = result?.find((r) => r.isActive)
          if (activeRuleset) {
            setSelectedRulesId(activeRuleset._id)
          } else if (result?.length > 0) {
            setSelectedRulesId(result[0]._id)
          }
        } catch (err) {
          console.error('Error fetching rulesets:', err)
        } finally {
          setLoadingRulesets(false)
        }
      }
      fetchRulesets()
    }, [client])

    const handleRegenerate = useCallback(async () => {
      if (!documentId) {
        setError('Document must be saved first')
        return
      }

      if (fitmentTypeIndex === null || listingIndex === null) {
        setError('Could not determine listing position. Please try refreshing the page.')
        return
      }

      setLoading(true)
      setError('')
      setResults(null)

      const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'
      try {
        const res = await fetch(`${baseURL}/api/ai/generate-descriptions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            buyerGuideId: documentId,
            fitmentTypeIndex,
            listingIndices: [listingIndex],
            force: forceRegenerate,
            rulesId: selectedRulesId || undefined,
          }),
        })

        if (!res.ok) {
          const data = await res.json()
          console.error('[ListingAIRegenerate] Error response:', data)
          throw new Error(data.message || 'Failed to generate descriptions')
        }

        const data: GenerationResponse = await res.json()
        setResults(data)
      } catch (err: any) {
        console.error('[ListingAIRegenerate] Caught error:', err)
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          setError(
            `Cannot connect to server. Make sure the frontend server is running at ${baseURL}`
          )
        } else {
          setError(err.message || 'Error generating descriptions')
        }
      } finally {
        setLoading(false)
      }
    }, [documentId, fitmentTypeIndex, listingIndex, forceRegenerate, selectedRulesId])

    // Check if we have valid indices
    const hasValidIndices = fitmentTypeIndex !== null && listingIndex !== null

    return (
      <Stack gap={3} ref={ref}>
        <Card padding={4} radius={2} shadow={1} tone="primary">
          <Stack gap={3}>
            <Flex align="center" gap={2}>
              <SyncIcon />
              <Text size={1} weight="semibold">
                Regenerate AI Content
              </Text>
            </Flex>

            <Text size={1} muted>
              Generate or regenerate AI descriptions for this fitment listing only.
            </Text>

            {/* Ruleset Selection */}
            <Box>
              <Label size={0} style={{ marginBottom: '6px', display: 'block' }}>
                AI Rules
              </Label>
              {loadingRulesets ? (
                <Flex align="center" gap={2}>
                  <Spinner />
                  <Text size={1} muted>
                    Loading rulesets...
                  </Text>
                </Flex>
              ) : rulesets.length === 0 ? (
                <Text size={1} muted>
                  No rulesets configured
                </Text>
              ) : (
                <Select
                  value={selectedRulesId}
                  onChange={(e) => setSelectedRulesId(e.currentTarget.value)}
                  fontSize={1}
                >
                  {rulesets.map((rs) => (
                    <option key={rs._id} value={rs._id}>
                      {rs.name} (v{rs.version}){rs.isActive ? ' - Active' : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Box>

            {/* Force regenerate option */}
            <Flex align="center" gap={2}>
              <Checkbox
                checked={forceRegenerate}
                onChange={(e) => setForceRegenerate(e.currentTarget.checked)}
              />
              <Text size={1}>Force regenerate (overwrite existing)</Text>
            </Flex>

            {/* Generate button */}
            <Button
              text={loading ? 'Generating...' : 'Regenerate AI Content'}
              tone="positive"
              icon={SyncIcon}
              onClick={handleRegenerate}
              disabled={loading || !documentId || !hasValidIndices || loadingRulesets}
              style={{ width: '100%' }}
            />

            {/* Status messages */}
            {!documentId && (
              <Text size={1} muted>
                Save the document first to generate descriptions.
              </Text>
            )}

            {!hasValidIndices && documentId && (
              <Text size={1} muted>
                Could not determine listing position in the document.
              </Text>
            )}
          </Stack>
        </Card>

        {/* Error display */}
        {error && (
          <Card padding={3} radius={2} tone="critical">
            <Text size={1}>{error}</Text>
          </Card>
        )}

        {/* Loading indicator */}
        {loading && (
          <Card padding={3} radius={2}>
            <Flex align="center" justify="center" gap={3}>
              <Spinner />
              <Text size={1}>Generating AI content for this listing...</Text>
            </Flex>
          </Card>
        )}

        {/* Results display */}
        {results && (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={3}>
              <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
                <Text size={1} weight="semibold">
                  Generation Results
                </Text>
                <Flex gap={2}>
                  <Badge tone="positive">{results.summary.generated} generated</Badge>
                  {results.summary.skipped > 0 && (
                    <Badge tone="caution">{results.summary.skipped} skipped</Badge>
                  )}
                  {results.summary.failed > 0 && (
                    <Badge tone="critical">{results.summary.failed} failed</Badge>
                  )}
                </Flex>
              </Flex>

              {/* Individual description results */}
              {results.results.length > 0 && (
                <Stack gap={2}>
                  {Object.entries(results.results[0].descriptions).map(([descType, desc]) => (
                    <Flex key={descType} align="center" justify="space-between" gap={2}>
                      <Text size={1}>{DESCRIPTION_TYPE_LABELS[descType] || descType}</Text>
                      {desc.error ? (
                        <Badge tone="critical">Error</Badge>
                      ) : desc.skipped ? (
                        <Badge tone="caution">Skipped</Badge>
                      ) : desc.generated ? (
                        <Badge tone="positive">Generated</Badge>
                      ) : (
                        <Badge tone="default">-</Badge>
                      )}
                    </Flex>
                  ))}
                </Stack>
              )}

              {/* Metadata */}
              <Box paddingTop={2}>
                <Flex gap={3} wrap="wrap">
                  {results.rules && (
                    <Text size={0} muted>
                      Rules: {results.rules.name} (v{results.rules.version})
                    </Text>
                  )}
                  <Text size={0} muted>
                    Cost: ${results.metadata.totalCost.toFixed(4)}
                  </Text>
                  <Text size={0} muted>
                    Tokens: {results.metadata.totalTokens.toLocaleString()}
                  </Text>
                </Flex>
              </Box>

              {/* Success message */}
              <Card padding={3} radius={2} tone="positive">
                <Text size={1}>
                  Changes saved to draft. Review the AI Content section and publish when ready.
                </Text>
              </Card>
            </Stack>
          </Card>
        )}
      </Stack>
    )
  }
)

ListingAIRegenerateButton.displayName = 'ListingAIRegenerateButton'

export default ListingAIRegenerateButton
